import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TableCell,
  TableRow,
} from "@mui/material";
import { Colors } from "../../constants/Colors";
import Card from "../../components/Card";
import CustomTable from "../../components/Table";
import CustomButton from "../../components/Button";
import CustomInput from "../../components/Input";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { apiRequest } from "../../services/api-request";
import Pagination from "../../components/Pagination";
import { useAppContext } from "../../context/AppContext";
import { downloadFile, escapeCSVField } from "../../utilities/exportToCsv";

const dashboardHeader = [
  {
    id: 1,
    title: "ID",
  },
  {
    id: 2,
    title: "Name",
  },
  {
    id: 3,
    title: "Mobile",
  },
  {
    id: 4,
    title: "User Type",
  },
  {
    id: 5,
    title: "IP",
  },
  {
    id: 6,
    title: "Login",
  },
  {
    id: 7,
    title: "Logout",
  },
];

const States = () => {
  const { user } = useAppContext();

  const [logList, setLogList] = useState({
    result: [],
    totalCount: "",
    loader: false,
  });

  const limit = 10;
  const [skip, setSkip] = useState(0);

  const [logListCSV, setLogListCSV] = useState({
    data: [],
  });

  useEffect(() => {
    getLogs({ skip, searchText: "" });
  }, [skip]);

  const getLogs = ({ skip, searchText }) => {
    setLogList({ loader: true });
    const payload = {
      skip: skip,
      limit: limit,
      ...(searchText && {
        searchText: searchText,
      }),
    };

    apiRequest({
      url: "auth/get-login-logs",
      method: "POST",
      data: payload,
    })
      .then((res) => {
        setLogList({
          loader: false,
          totalCount: res.count ? res.count : res.data.length,
          result: res.data,
        });
      })
      .catch((err) => {
        setLogList({ loader: false });
      });
  };

  const handlePageChange = (pageNumber) => {
    const newSkip = (pageNumber - 1) * limit;
    setSkip(newSkip);
  };

  const TableDataUi = () => {
    return logList?.result?.map((row, index) => (
      <TableRow
        key={row.id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.id}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.mobile}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.user_type}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.ip}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.login_date}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            fontFamily={"Poppins-Regular"}
            color={Colors.textColor}
            fontSize={12}
          >
            {row.logout_date}
          </Typography>
        </TableCell>
      </TableRow>
    ));
  };

  const handleSearchTextChange = (e) => {
    const searchText = e.target.value;
    getLogs({ searchText });
  };

  useEffect(() => {
    let headers = ["Id,Name,Mobile,User Type,IP,Login,Logout"];

    let logsCsv;
    if (Array.isArray(logListCSV.data) && logListCSV.data.length > 0) {
      logsCsv = logListCSV.data?.reduce((acc, log, index) => {
        const { id, name, mobile, user_type, ip, login_date, logout_date } = log;
        acc.push([
          escapeCSVField(id),
          escapeCSVField(name),
          escapeCSVField(mobile),
          escapeCSVField(user_type),
          escapeCSVField(ip),
          escapeCSVField(login_date),
          escapeCSVField(logout_date),
        ].join(","));
        return acc;
      }, []);

      downloadFile({
        data: [...headers, ...logsCsv].join("\n"),
        fileName: "Login Logs.csv",
        fileType: "text/csv",
      });
    }
  }, [logListCSV]);

  const exportToCsv = () => {
    let payload = {
      skip: 0,
      limit: 100000000,
    };
    apiRequest({
      url: `auth/get-login-logs`,
      data: payload,
      method: "POST",
    })
      .then((res) => {
        setLogListCSV({
          data: res?.data,
        });
      })
      .catch((err) => {
        alert(err);
      });
  };

  return (
    <Box>
      <Grid container justifyContent={"end"}>
        <CustomButton
          title={"Export List"}
          handleButtonClick={() => exportToCsv()}
          backgroundColor={Colors.headerColor}
          textColor={Colors.white}
          textFontSize={14}
          padding={"5px 10px"}
        />
      </Grid>
      <Grid
        container
        alignItems={"center"}
        justifyContent={"space-between"}
        mt={2}
      >
        <Grid item>
          <Typography
            fontFamily={"Poppins-Medium"}
            fontSize={20}
            color={Colors.textColor}
          >
            Login Logs
          </Typography>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Card>
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Grid item md={3}>
              <CustomInput
                placeholder={"Search"}
                onChange={(e) => handleSearchTextChange(e)}
                leftIcon={
                  <SearchOutlinedIcon style={{ color: Colors.textColor }} />
                }
              />
            </Grid>
          </Grid>
          <Box mt={2}>
            <CustomTable
              headerData={dashboardHeader}
              tableData={logList.result}
            >
              <TableDataUi />
            </CustomTable>
            <Box mt={2} display={"flex"} justifyContent={"right"}>
              {logList.totalCount > 10 && (
                <Pagination
                  totalCount={logList.totalCount}
                  skip={skip}
                  limit={limit}
                  onPageChange={handlePageChange}
                />
              )}
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default States;
